import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useApp } from '../context/AppContext'
import { calculateInches, calculateAcreInches } from '../utils/calculations'
import { meterUnits, getReadingHelperText, formatReadingValue } from '../utils/constants'
import { UI, inputClass } from '../utils/styles'
import { ButtonPrimary } from '../components/ui/Button'
import { Camera } from '../components/ui/Icons'
import { DecimalInput } from '../components/ui/DecimalInput'

export function AddReadingPage() {
  const {
    meters,
    session,
    mode,
    setMode,
    selectedMeterId,
    setSelectedMeterId,
    selectedMeter,
    lastReading,
    showSeasonStartCheckbox,
    markAsSeasonStart,
    setMarkAsSeasonStart,
    enteredBy,
    setEnteredBy,
    newReading,
    setNewReading,
    readingDateTime,
    setReadingDateTime,
    calcAcres,
    setCalcAcres,
    calcUnits,
    setCalcUnits,
    setShowQuickAddMeter,
    readingPhotoPreview,
    setReadingPhotoFile,
    setReadingPhotoPreview,
    handleReadingPhotoSelect,
    handleAddReading,
    savingReading,
    isReadingOutsideSeason,
  } = useApp()

  const [showEditMeta, setShowEditMeta] = useState(false)
  const [meterSearchText, setMeterSearchText] = useState('')
  const [showMeterDropdown, setShowMeterDropdown] = useState(false)
  const meterSearchRef = useRef(null)
  const dateWrapperRef = useRef(null)
  const [dateInputWidth, setDateInputWidth] = useState(null)

  // Measure parent width for datetime input (iOS Safari workaround)
  useEffect(() => {
    if (showEditMeta && dateWrapperRef.current) {
      setDateInputWidth(dateWrapperRef.current.offsetWidth)
    }
  }, [showEditMeta])

  // Close dropdown when tapping outside
  useEffect(() => {
    const handleTap = (e) => {
      if (meterSearchRef.current && !meterSearchRef.current.contains(e.target)) {
        setShowMeterDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleTap)
    return () => document.removeEventListener('mousedown', handleTap)
  }, [])

  // Memoized so typing a reading digit doesn't re-sort the meter list
  const sortedMeters = useMemo(
    () => [...meters].sort((a, b) => a.name.localeCompare(b.name)),
    [meters]
  )
  const filteredMeters = useMemo(
    () => meterSearchText
      ? sortedMeters.filter(m => m.name.toLowerCase().includes(meterSearchText.toLowerCase()))
      : sortedMeters,
    [sortedMeters, meterSearchText]
  )

  const userName = session?.user?.user_metadata?.first_name || ''

  // Format current reading date/time for display
  const formatReadingDateTime = () => {
    if (!readingDateTime) {
      const time = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      return `Today, ${time}`
    }
    const dt = new Date(readingDateTime)
    const now = new Date()
    const isToday = dt.toDateString() === now.toDateString()
    const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    if (isToday) return `Today, ${time}`
    return `${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
  }

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return
    setNewReading({ start: '', end: '', notes: '' })
    if (newMode === 'calculator') setCalcAcres('')
    setMode(newMode)
  }

  // Calculate display values
  let calculatedInches = ''
  let calculatedAcreInches = ''
  let seasonTotalInches = ''
  let seasonTotalAcreInches = ''

  if (mode === 'calculator' && newReading.start && newReading.end && calcAcres) {
    calculatedInches = calculateInches(newReading.start, newReading.end, calcAcres, calcUnits)
    calculatedAcreInches = calculateAcreInches(calculatedInches, calcAcres)
  } else if (selectedMeter && newReading.end) {
    const previousEnd = lastReading?.end ?? selectedMeter.season_starting_reading
    if (previousEnd != null) {
      calculatedInches = calculateInches(previousEnd, newReading.end, selectedMeter.acres, selectedMeter.units)
      calculatedAcreInches = calculateAcreInches(calculatedInches, selectedMeter.acres)

      if (selectedMeter.season_starting_reading != null) {
        seasonTotalInches = calculateInches(selectedMeter.season_starting_reading, newReading.end, selectedMeter.acres, selectedMeter.units)
        seasonTotalAcreInches = calculateAcreInches(seasonTotalInches, selectedMeter.acres)
      }
    } else {
      calculatedInches = '0.00'
      calculatedAcreInches = '0.00'
      seasonTotalInches = '0.00'
      seasonTotalAcreInches = '0.00'
    }
  }

  return (
    <div className="px-4 md:px-6 max-w-2xl mx-auto py-4">
      {/* Header */}
      <h2 className="text-lg md:text-xl font-semibold text-slate-800 tracking-tight mb-4">Add Reading</h2>

      {/* Segmented Control */}
      <div className="bg-slate-100 rounded-full p-1 flex mb-6">
        <button
          onClick={() => handleModeSwitch('record')}
          className={`flex-1 py-2 text-sm font-medium rounded-full text-center transition-all ${mode === 'record' ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500'}`}
        >
          Record
        </button>
        <button
          onClick={() => handleModeSwitch('calculator')}
          className={`flex-1 py-2 text-sm font-medium rounded-full text-center transition-all ${mode === 'calculator' ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500'}`}
        >
          Calculator
        </button>
      </div>

      {/* Form Fields */}
      <div className="space-y-5">
        {mode === 'record' && (
          <>
            {/* Meter Select */}
            <div ref={meterSearchRef} className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Meter Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={meterSearchText || (selectedMeter ? selectedMeter.name : '')}
                  onChange={(e) => {
                    setMeterSearchText(e.target.value)
                    setSelectedMeterId(null)
                    setShowMeterDropdown(true)
                  }}
                  onFocus={() => setShowMeterDropdown(true)}
                  className={inputClass}
                  placeholder="Search meters..."
                />
                {(meterSearchText || selectedMeterId || showMeterDropdown) && (
                  <button onClick={() => { setMeterSearchText(''); setSelectedMeterId(null); setShowMeterDropdown(false) }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
              {showMeterDropdown && !selectedMeterId && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {filteredMeters.map(meter => (
                    <button
                      key={meter.id}
                      onClick={() => {
                        setSelectedMeterId(meter.id)
                        setMeterSearchText('')
                        setShowMeterDropdown(false)
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                    >
                      {meter.name}
                    </button>
                  ))}
                  {filteredMeters.length === 0 && meterSearchText && (
                    <p className="px-4 py-3 text-sm text-slate-400">No meters found</p>
                  )}
                  <button
                    onClick={() => { setShowQuickAddMeter(true); setShowMeterDropdown(false) }}
                    className="w-full text-left px-4 py-3 text-sm text-sky-700 font-medium hover:bg-sky-50 border-t border-slate-100"
                  >
                    + Add New Meter
                  </button>
                </div>
              )}
            </div>

            {/* Current Reading */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Current Reading</label>
              {selectedMeter && lastReading && (
                <p className="text-sm text-slate-400 mb-1.5">Previous reading: {formatReadingValue(lastReading.end, selectedMeter.units)}</p>
              )}
              {selectedMeter && !lastReading && selectedMeter.season_starting_reading != null && (
                <p className="text-sm text-slate-400 mb-1.5">Season start: {formatReadingValue(selectedMeter.season_starting_reading, selectedMeter.units)}</p>
              )}
              <DecimalInput
                value={newReading.end}
                onChange={(e) => setNewReading({...newReading, end: e.target.value})}
                units={selectedMeter?.units}
                className={inputClass}
                placeholder="Enter current meter reading"
              />
              {selectedMeter && getReadingHelperText(selectedMeter.units) && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">{getReadingHelperText(selectedMeter.units)}</div>
              )}
            </div>

            {/* Season Start Checkbox */}
            {showSeasonStartCheckbox && (
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markAsSeasonStart}
                  onChange={(e) => setMarkAsSeasonStart(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span className="text-sm text-slate-600">Set as season start reading</span>
              </label>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes (Optional)</label>
              <input type="text" value={newReading.notes} onChange={(e) => setNewReading({...newReading, notes: e.target.value})} className={inputClass} placeholder="Any additional notes..." />
            </div>

            {/* Photo (inline button) */}
            <div>
              {readingPhotoPreview ? (
                <div className="flex items-center gap-3">
                  <img src={readingPhotoPreview} alt="Reading preview" className="w-12 h-12 object-cover rounded-xl" />
                  <button type="button" onClick={() => { setReadingPhotoFile(null); setReadingPhotoPreview(null) }} className="text-sm text-red-500">Remove</button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 cursor-pointer text-sky-700 hover:text-sky-800 transition-colors">
                  <Camera size={20} />
                  <span className="text-sm">Add Photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handleReadingPhotoSelect} className="hidden" />
                </label>
              )}
            </div>

            {/* Entered by / Date & Time (combined line) */}
            <div>
              {showEditMeta ? (
                <div className="space-y-3" ref={dateWrapperRef}>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Entered By</label>
                    <input type="text" value={enteredBy} onChange={(e) => setEnteredBy(e.target.value)} className={inputClass} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Date & Time</label>
                    <input type="datetime-local" value={readingDateTime} onChange={(e) => setReadingDateTime(e.target.value)} className={inputClass} style={dateInputWidth ? { width: `${dateInputWidth}px` } : undefined} />
                    {isReadingOutsideSeason(readingDateTime) && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800 flex items-start gap-2">
                        <span>⚠️</span>
                        <span>This reading falls outside of the current active season.</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-sm text-slate-400">Entered by {enteredBy || userName || 'Unknown'}</span>
                  <span className="text-sm text-slate-300">·</span>
                  <span className="text-sm text-slate-400">{formatReadingDateTime()}</span>
                  <button
                    onClick={() => {
                      if (!enteredBy && userName) setEnteredBy(userName)
                      setShowEditMeta(true)
                    }}
                    className="text-sm text-sky-700 hover:text-sky-800 ml-1"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {mode === 'calculator' && (
          <div className="space-y-5">
            <p className="text-sm font-semibold text-slate-800">Calculator Mode - Results will not be saved</p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Units</label>
              <select value={calcUnits} onChange={(e) => setCalcUnits(e.target.value)} className={inputClass}>
                {meterUnits.map(unit => (<option key={unit} value={unit}>{unit}</option>))}
              </select>
              {getReadingHelperText(calcUnits) && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">{getReadingHelperText(calcUnits)}</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Previous Reading</label>
                <DecimalInput value={newReading.start} onChange={(e) => setNewReading({...newReading, start: e.target.value})} units={calcUnits} className={inputClass} placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Current Reading</label>
                <DecimalInput value={newReading.end} onChange={(e) => setNewReading({...newReading, end: e.target.value})} units={calcUnits} className={inputClass} placeholder="5000" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Acres</label>
              <input type="number" value={calcAcres} onChange={(e) => setCalcAcres(e.target.value)} className={inputClass} placeholder="130" />
            </div>
          </div>
        )}

        {/* Results */}
        {((mode === 'record' && selectedMeter && newReading.end) || (mode === 'calculator' && newReading.start && newReading.end && calcAcres)) && (
          markAsSeasonStart && mode === 'record' ? (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-sm text-sky-800">
              This reading will be saved as the season start baseline for <strong>{selectedMeter.name}</strong>.
            </div>
          ) : (
            <div className={`${UI.glass} ${UI.border} ${UI.radius} p-4 md:p-5 ${UI.shadow} space-y-3`}>
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide text-center">{mode === 'record' ? 'This Reading' : 'Results'}</p>
                <div className="grid grid-cols-2 gap-3 md:gap-8">
                  <div className="text-center">
                    <p className="text-xs md:text-sm font-medium text-slate-800/80 mb-1">Inches Applied</p>
                    <p className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">{calculatedInches}"</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs md:text-sm font-medium text-slate-800/80 mb-1">Acre-Inches</p>
                    <p className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">{calculatedAcreInches}</p>
                  </div>
                </div>
              </div>
              {mode === 'record' && seasonTotalInches && (
                <>
                  <div className="border-t border-slate-200"></div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide text-center">Season Total</p>
                    <div className="grid grid-cols-2 gap-3 md:gap-8">
                      <div className="text-center">
                        <p className="text-xs md:text-sm font-medium text-slate-800/80 mb-1">Total Inches</p>
                        <p className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">{seasonTotalInches}"</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs md:text-sm font-medium text-slate-800/80 mb-1">Total Acre-Inches</p>
                        <p className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight">{seasonTotalAcreInches}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        )}

        {/* Submit */}
        {mode === 'record' && (
          <ButtonPrimary onClick={handleAddReading} disabled={!selectedMeterId || !newReading.end || savingReading} className="w-full">
            {savingReading ? 'Saving…' : 'Save Reading'}
          </ButtonPrimary>
        )}
      </div>
    </div>
  )
}
